import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Brain, Network, Boxes, RefreshCw, Users, Workflow } from "lucide-react";
import { TID } from "../../lib/testIds";

const SOLUTIONS = [
  {
    n: "01",
    icon: Brain,
    title: "AI Product Engineering",
    desc: "Turn AI opportunities into production-ready product capabilities. We design and build intelligent features, copilots, document systems, AI-powered workflows, natural-language interfaces, and LLM-enabled product experiences.",
    cta: "Explore AI Product Engineering",
    slug: "ai-product-engineering",
    span: "md:col-span-2",
  },
  {
    n: "02",
    icon: Network,
    title: "AI Agents & RAG Systems",
    desc: "Build AI systems grounded in your business knowledge and workflows. We engineer retrieval systems, knowledge assistants, tool-using agents, permission-aware AI experiences, and multi-step intelligent workflows.",
    cta: "Explore AI Agents & RAG",
    slug: "ai-agents-rag",
    span: "md:col-span-1",
  },
  {
    n: "03",
    icon: Boxes,
    title: "SaaS Product Development",
    desc: "Build SaaS products designed to evolve beyond the first release. From architecture and multi-tenancy to billing, integrations, analytics, cloud infrastructure, and AI capabilities.",
    cta: "Explore SaaS Development",
    slug: "saas-product-development",
    span: "md:col-span-1",
  },
  {
    n: "04",
    icon: RefreshCw,
    title: "SaaS Modernization",
    desc: "Modernize aging platforms without blindly rewriting everything. Improve legacy applications, architecture, cloud infrastructure, deployment workflows, and maintainability through practical strategies.",
    cta: "Explore SaaS Modernization",
    slug: "saas-modernization",
    span: "md:col-span-1",
  },
  {
    n: "05",
    icon: Users,
    title: "Dedicated Product Teams",
    desc: "Expand engineering capacity without months of recruiting. Deploy experienced engineers and product delivery capability aligned with your roadmap, architecture, and internal processes.",
    cta: "Build Your Product Team",
    slug: "dedicated-product-teams",
    span: "md:col-span-1",
  },
  {
    n: "06",
    icon: Workflow,
    title: "Workflow Automation",
    desc: "Replace repetitive operations with connected, intelligent workflows. We combine APIs, business rules, AI, cloud services, integrations, and human approvals to automate complex business processes.",
    cta: "Explore Workflow Automation",
    slug: "workflow-automation",
    span: "md:col-span-2",
  },
];

const Card = ({ item, i }) => {
  const Icon = item.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
      className={item.span}
    >
      <Link
        to={`/solutions/${item.slug}`}
        data-testid={TID.solutionCard(i)}
        className="group relative bg-[#0A0A0A] hover:bg-[#111] transition-colors p-6 md:p-10 flex flex-col gap-6 min-h-[340px] cursor-pointer h-full"
      >
        <div className="absolute left-0 top-0 h-full w-[2px] bg-[#FF5F1F] scale-y-0 origin-top group-hover:scale-y-100 transition-transform duration-500" />

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              /{item.n}
            </span>
            <div className="w-10 h-10 flex items-center justify-center border border-white/10 bg-white/[0.02] group-hover:border-[#FF5F1F] group-hover:text-[#FF5F1F] text-zinc-400 transition-colors">
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
          </div>
          <ArrowUpRight className="w-5 h-5 text-zinc-600 group-hover:text-[#FF5F1F] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
        </div>

        <div className="flex-1 flex flex-col gap-3">
          <h3 className="font-headings text-2xl md:text-3xl font-bold tracking-tight text-white leading-tight">
            {item.title}
          </h3>
          <p className="text-sm md:text-base text-zinc-400 leading-relaxed max-w-xl">{item.desc}</p>
        </div>

        <div className="pt-4 border-t border-white/5">
          <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 group-hover:text-[#FF5F1F] transition-colors inline-flex items-center gap-2">
            {item.cta} →
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

export const CoreSolutions = () => {
  return (
    <section
      id="solutions"
      data-testid={TID.coreSolutions}
      className="relative bg-[#050505] py-24 md:py-32 border-y border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-[#FF5F1F]" />
              <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
                What We Do
              </span>
            </div>
            <h2 className="font-headings text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white max-w-3xl">
              Engineering Capabilities Built Around <em className="text-[#FF5F1F] not-italic">Real</em> Product Challenges
            </h2>
          </div>
          <p className="text-sm md:text-base text-zinc-400 max-w-md leading-relaxed">
            Whether you are introducing AI, building a SaaS product, modernizing an existing
            platform, or expanding engineering capacity — we bring together the capabilities
            required to move from strategy to production.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {SOLUTIONS.map((item, i) => (
            <Card key={i} item={item} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
